import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Select,
  Tag,
  Drawer,
  Timeline,
  Divider,
  Row,
  Col,
  Avatar,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { EditOutlined, DeleteOutlined, PlusOutlined, MessageOutlined } from '@ant-design/icons';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  addContactComment,
} from '../services/contactsService';
import { getCurrentUser } from '../services/authService';
import { getUserProfile } from '../services/userService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;

function ContactsAdmin() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [form] = Form.useForm();
  const chatContainerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const data = await getContacts();
      setContacts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this contact?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteContact(id);
          fetchContacts();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleOpenComments = contact => {
    setSelectedContact(contact);
    setCommentDrawerOpen(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addContactComment(selectedContact.id, newComment);
      await fetchContacts();
      setNewComment('');

      // Scroll to bottom after new comment is added
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    // Scroll to bottom when comments change or drawer opens
    if (chatContainerRef.current && commentDrawerOpen) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [selectedContact?.contact_comments, commentDrawerOpen]);

  const getStatusColor = status => {
    const colors = {
      active: 'green',
      inactive: 'orange',
      archived: 'red',
    };
    return colors[status] || 'default';
  };

  const getTypeColor = type => {
    const colors = {
      client: 'blue',
      vendor: 'purple',
      partner: 'green',
      lead: 'orange',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.first_name} ${record.last_name}`,
      sorter: (a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: type => <Tag color={getTypeColor(type)}>{type?.toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<MessageOutlined />}
            onClick={() => handleOpenComments(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/contacts/${record.id}`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Contacts</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/contacts/new')}
        >
          Add Contact
        </Button>
      </div>

      <Table columns={columns} dataSource={contacts} rowKey="id" />

      <Drawer
        title={`Comments - ${selectedContact?.first_name} ${selectedContact?.last_name}`}
        placement="right"
        onClose={() => setCommentDrawerOpen(false)}
        open={commentDrawerOpen}
        width={500}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 120px)',
          }}
        >
          <div
            ref={chatContainerRef}
            className="chat-container"
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              padding: '12px',
            }}
          >
            {selectedContact?.contact_comments
              ?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              ?.map((comment, index) => (
                <div
                  key={comment.id || index}
                  className="chat-message"
                  style={{
                    marginBottom: '16px',
                    maxWidth: '80%',
                    alignSelf: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {index % 2 === 0 ? (
                      <Avatar
                        size="small"
                        style={{
                          marginRight: '8px',
                          backgroundColor: '#87d068',
                        }}
                        icon={<UserOutlined />}
                      >
                        {comment.user_email
                          ? comment.user_email.substring(0, 2).toUpperCase()
                          : 'U'}
                      </Avatar>
                    ) : null}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#1890ff',
                          color: index % 2 === 0 ? 'inherit' : 'white',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        }}
                      >
                        <div>{comment.comment}</div>
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#8c8c8c',
                          marginTop: '4px',
                          textAlign: index % 2 === 0 ? 'left' : 'right',
                        }}
                      >
                        {index % 2 === 0 ? (
                          <span>
                            {comment.user_email || 'User'} •{' '}
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        ) : (
                          <span>{new Date(comment.created_at).toLocaleString()} • You</span>
                        )}
                      </div>
                    </div>
                    {index % 2 === 1 ? (
                      <Avatar
                        size="small"
                        style={{
                          marginLeft: '8px',
                          backgroundColor: '#1890ff',
                        }}
                      >
                        You
                      </Avatar>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
          <div
            style={{
              padding: '12px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <TextArea
                rows={2}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Type a message..."
                style={{
                  borderRadius: '18px',
                  padding: '10px 14px',
                  resize: 'none',
                  flexGrow: 1,
                  marginRight: '8px',
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey && newComment.trim()) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
              />
              <Button
                type="primary"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                shape="circle"
                icon={<span>➤</span>}
                style={{ marginBottom: '4px' }}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default ContactsAdmin;
